import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  chatSession: {
    _id: null,
    userId: null,
    orgId: null,
    messages: [],
    updates: [],
    recents: [],
    trails: [],
    fileUploads: [],
    createdAt: null,
    updatedAt: null,
  },
};

const findTrail = (state, id) =>
  state.chatSession.trails.find((t) => t.trailId === id);

function mergeOutputData(target = {}, patch = {}) {
  const out = target;
  for (const [k, v] of Object.entries(patch)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = mergeOutputData(out[k] || {}, v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function upsertEntryForMessage(layout, messageId) {
  if (!layout || !Array.isArray(layout.gridLayout)) return null;
  let entry = layout.gridLayout.find((e) => e.messageId === messageId);
  if (!entry) {
    entry = {
      messageId,
      lg: { x: 0, y: 0, w: 12, h: 6 },
      md: { x: 0, y: 0, w: 12, h: 6 },
      sm: { x: 0, y: 0, w: 12, h: 6 },
      xs: { x: 0, y: 0, w: 12, h: 6 },
      xxs: { x: 0, y: 0, w: 12, h: 6 },
    };
    layout.gridLayout.push(entry);
  }
  return entry;
}

function toIntOr(val, fallback) {
  const n = Number(val);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}
function sanitizeRectMinimal(rect, fallback = { x: 0, y: 0, w: 1, h: 1 }) {
  const x = Math.max(0, toIntOr(rect?.x, fallback.x));
  const y = Math.max(0, toIntOr(rect?.y, fallback.y));
  const w = Math.max(1, toIntOr(rect?.w, fallback.w));
  const h = Math.max(1, toIntOr(rect?.h, fallback.h));
  return { x, y, w, h };
}

const sessionSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatSession(state, { payload }) {
      state.chatSession = payload;
    },

    addUserMessage(state, { payload }) {
      state.chatSession.messages.push(payload);
      state.chatSession.updatedAt = new Date().toISOString();
    },
    addSystemMessage(state, { payload }) {
      const msg = { sender: 'system', ...payload };
      state.chatSession.messages.push(msg);
      state.chatSession.updatedAt = new Date().toISOString();
    },
    updateMessageById(state, { payload }) {
      const { messageId, updates } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (msg) Object.assign(msg, updates);
      state.chatSession.updatedAt = new Date().toISOString();
    },
    deleteMessageById(state, { payload }) {
      const { messageId } = payload;
      state.chatSession.messages = state.chatSession.messages.filter((m) => m.messageId !== messageId);
      state.chatSession.updatedAt = new Date().toISOString();
    },

    addToFieldUpdates(state, { payload }) {
      state.chatSession.updates.push(payload);
    },
    replaceRecents(state, { payload }) {
      state.chatSession.recents = payload;
      state.chatSession.updatedAt = new Date().toISOString();
    },

    updateInlineRecordInArray(state, { payload }) {
      const { messageId, recordId, updatedRecord } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (!msg || !Array.isArray(msg.content)) return;
      msg.content = msg.content.map((r) => (r.Id === recordId ? updatedRecord : r));
    },

    addToMetacontentByMessageId(state, { payload }) {
      const { messageId, key, value } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (!msg) return;
      msg.metacontent = msg.metacontent || {};
      msg.metacontent[key] = value;
      state.chatSession.updatedAt = new Date().toISOString();
    },

    addColumnToMsgContent(state, { payload }) {
      const { messageId, fieldName, updates } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (!msg || !Array.isArray(msg.content)) return;
      const map = Object.fromEntries(updates.map((u) => [u.Id, u[fieldName]]));
      msg.content = msg.content.map((row) => ({
        ...row,
        [fieldName]: map[row.Id] !== undefined ? map[row.Id] : row[fieldName] ?? null,
      }));
      state.chatSession.updatedAt = new Date().toISOString();
    },
    addColumnToMetacontent(state, { payload }) {
      const { messageId, fieldName } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (!msg) return;
      msg.metacontent = msg.metacontent || {};
      msg.metacontent.columnOrder = msg.metacontent.columnOrder || [];
      if (!msg.metacontent.columnOrder.some((c) => c.field === fieldName)) {
        msg.metacontent.columnOrder.push({ field: fieldName });
      }
      state.chatSession.updatedAt = new Date().toISOString();
    },
    removeColumnFromMsg(state, { payload }) {
      const { messageId, fieldName } = payload;
      const msg = state.chatSession.messages.find((m) => m.messageId === messageId);
      if (!msg || !Array.isArray(msg.content)) return;
      msg.content.forEach((row) => delete row[fieldName]);
      msg.metacontent = msg.metacontent || {};
      msg.metacontent.columnOrder = (msg.metacontent.columnOrder || []).filter((c) => c.field !== fieldName);
      state.chatSession.updatedAt = new Date().toISOString();
    },

    addTrail(state, { payload }) {
      const existing = findTrail(state, payload.trailId);
      if (existing) Object.assign(existing, payload);
      else state.chatSession.trails.push(payload);
    },
    updateTrail(state, { payload }) {
      const {
        trailId,
        trailStepId,
        status,
        steps,
        activeStep,
        outputData,
        explainer,
        timeCompleted,
        timeToComplete, // NEW: accept ms from backend
        agent,
        type,
      } = payload;

      const trail = state.chatSession.trails.find((t) => t.trailId === trailId);
      if (!trail) return;

      let touched = false;

      if (trailStepId !== undefined) {
        const ts = trail.trailSteps.find((s) => s.trailStepId === trailStepId);
        if (!ts) return;

        if (steps) { ts.steps = steps; touched = true; }
        if (activeStep !== undefined) { ts.activeStep = activeStep; touched = true; }
        if (status) { ts.status = status; touched = true; }
        if (outputData && typeof outputData === 'object') {
          ts.outputData = mergeOutputData(ts.outputData || {}, outputData);
          touched = true;
        }
        if (explainer !== undefined) { ts.explainer = explainer; touched = true; }
        if (timeCompleted !== undefined) { ts.timeCompleted = timeCompleted; touched = true; }

        // NEW: persist ms to the step; coerce safely in case it arrives as a string
        if (timeToComplete !== undefined) {
          const n = Number(timeToComplete);
          ts.timeToComplete = Number.isFinite(n) ? n : ts.timeToComplete ?? null; // NEW
          touched = true;
        }

        if (agent !== undefined) { ts.agent = agent; touched = true; }
        if (type !== undefined) { ts.type = type; touched = true; }
      } else {
        if (status) { trail.status = status; touched = true; }
      }

      if (touched) {
        state.chatSession.updatedAt = new Date().toISOString();
      }
    },
    addTrailStep(state, { payload }) {
      const { trailId, trailStep, trailSteps } = payload;
      const trail = state.chatSession.trails.find((t) => t.trailId === trailId);
      if (!trail) return;

      const incoming = trailSteps || (trailStep ? [trailStep] : []);
      if (!incoming.length) return;

      incoming.forEach((ns) => {
        const existing = trail.trailSteps.find((ts) => ts.trailStepId === ns.trailStepId);
        if (existing) {
          Object.assign(existing, ns);
        } else {
          trail.trailSteps.push(ns);
        }
      });

      trail.trailSteps.sort((a, b) => a.trailStepId - b.trailStepId);
    },
    resetTrails(state) {
      state.chatSession.trails = [];
    },

    updateUiLayout(state, { payload }) {
      state.chatSession.layout = payload;
      state.chatSession.updatedAt = new Date().toISOString();
    },

    updateGridItemRect(state, { payload }) {
      const { messageId, breakpoint = 'lg', rect } = payload;
      if (!messageId || !rect) return;

      if (!state.chatSession.layout) {
        state.chatSession.layout = { gridLayout: [] };
      }
      if (!Array.isArray(state.chatSession.layout.gridLayout)) {
        state.chatSession.layout.gridLayout = [];
      }

      const entry = upsertEntryForMessage(state.chatSession.layout, messageId);
      if (!entry) return;

      const prev = entry[breakpoint] || { x: 0, y: 0, w: 1, h: 1 };
      const sanitized = sanitizeRectMinimal(rect, prev);

      entry[breakpoint] = sanitized;
      state.chatSession.updatedAt = new Date().toISOString();
    },

    applyFileUploadPatches(state, { payload }) {
      const updates = Array.isArray(payload?.updates) ? payload.updates : [];
      if (!updates.length) return;

      if (!Array.isArray(state.chatSession.fileUploads)) {
        state.chatSession.fileUploads = [];
      }

      const byId = new Map(
        state.chatSession.fileUploads.filter((u) => u?.fileUploadId).map((u) => [u.fileUploadId, u])
      );

      for (const patch of updates) {
        const id = patch?.fileUploadId;
        if (!id) continue;
        const existing = byId.get(id);
        if (existing) {
          Object.assign(existing, patch);
        } else {
          state.chatSession.fileUploads.push({ ...patch });
        }
      }

      state.chatSession.updatedAt = new Date().toISOString();
    },
  },
});

export const {
  setChatSession,
  addUserMessage,
  addSystemMessage,
  updateMessageById,
  addToFieldUpdates,
  deleteMessageById,
  replaceRecents,
  updateInlineRecordInArray,
  addToMetacontentByMessageId,
  addColumnToMsgContent,
  addColumnToMetacontent,
  removeColumnFromMsg,
  addTrail,
  addTrailStep,
  updateTrail,
  resetTrails,
  updateUiLayout,
  updateGridItemRect,
  applyFileUploadPatches,
} = sessionSlice.actions;

export const selectTrails = (state) => state.chat.chatSession.trails;
export const selectTrailById = (id) => (state) =>
  state.chat.chatSession.trails.find((t) => t.trailId === id);

export default sessionSlice.reducer;